// The workspace switches the sidebar draws from, remembered per workspace in
// this browser, so a returning operator's sidebar is whole on its first
// paint instead of growing Todos, Tasks, Subagents and Automations once the
// settings answer (operator, 21 Oct 2026: "some options take a few seconds
// to load"). Pure: the hooks are in ./useNavFlags.
//
// What is kept, under localStorage `lattice.navFlags.v1.<customerId>`:
//
//   {"v": 1, "flags": {"todos_enabled": true, …}, "agents": 6}
//
// flags   the nav switches (NAV_FLAG_KEYS), booleans only
// agents  the workspace line's agent count ("· 6 agents")
//
// The live answer always wins: a remembered value only stands in while the
// settings load. A switch turned off since the last visit shows its row for
// that one round trip; the pages guard themselves on live data only, so a
// click in that moment never reaches a page that is off. Storage can be
// missing or throw (a private window, cleared site data): every read and
// write is in a try/catch, and nothing remembered means today's behaviour.

import type { CustomerSettingsFlag } from "@/api/customerSettings";

/** Every switch a nav row, a hub tab, a Settings section or the palette is gated on. */
export const NAV_FLAG_KEYS: readonly CustomerSettingsFlag[] = [
  "todos_enabled",
  "tasks_enabled",
  "subagents_enabled",
  "workflows_enabled",
  "jobs_enabled",
  "machines_enabled",
  "connections_enabled",
  // Apps (Code › Apps, Settings › Outside access with the other two).
  "apps_enabled",
  "deployments_enabled",
  "booking_pages_enabled",
];

export type KnownFlags = Partial<Record<CustomerSettingsFlag, boolean>>;

/** What one workspace left behind in this browser. */
export interface RememberedNav {
  flags?: KnownFlags;
  agents?: number;
}

/** The part of Storage this module uses (a stub in the selftest). */
export type NavStorage = Pick<Storage, "getItem" | "setItem">;

const VERSION = 1;

export function navFlagsStorageKey(customerId: string): string {
  return `lattice.navFlags.v${VERSION}.${customerId}`;
}

/** Only the nav switches of a settings answer, and only those that are booleans. */
export function pickNavFlags(settings: object | null | undefined): KnownFlags | undefined {
  if (!settings || typeof settings !== "object") return undefined;
  const source = settings as Record<string, unknown>;
  const out: KnownFlags = {};
  for (const key of NAV_FLAG_KEYS) {
    if (typeof source[key] === "boolean") out[key] = source[key] as boolean;
  }
  return out;
}

/** What a stored string says; undefined for nothing, garbage or another version. */
export function parseRemembered(raw: string | null | undefined): RememberedNav | undefined {
  if (!raw) return undefined;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) return undefined;
  const record = data as { v?: unknown; flags?: unknown; agents?: unknown };
  if (record.v !== VERSION) return undefined;
  const out: RememberedNav = {};
  if (record.flags && typeof record.flags === "object" && !Array.isArray(record.flags)) {
    out.flags = pickNavFlags(record.flags);
  }
  if (typeof record.agents === "number" && Number.isInteger(record.agents) && record.agents >= 0) {
    out.agents = record.agents;
  }
  return out.flags || out.agents !== undefined ? out : undefined;
}

/** The remembered switches alone (the scout's name for it). */
export function parseRememberedFlags(raw: string | null | undefined): KnownFlags | undefined {
  return parseRemembered(raw)?.flags;
}

/** The switches with a route of their own, never in the settings answer. */
const OWN_ROUTE: readonly CustomerSettingsFlag[] = ["machines_enabled", "connections_enabled"];

/**
 * The switches to draw with: the live answer whole once there is one, else
 * what was remembered. `machines_enabled` and `connections_enabled` are not
 * in the settings answer (each has a route of its own), so a live answer
 * without one keeps the remembered value.
 */
export function chooseNavFlags(live: KnownFlags | undefined, remembered: KnownFlags | undefined): KnownFlags | undefined {
  if (!live) return remembered;
  let out = live;
  for (const key of OWN_ROUTE) {
    if (live[key] === undefined && remembered?.[key] !== undefined) out = { ...out, [key]: remembered[key] };
  }
  return out;
}

function serialize(r: RememberedNav): string {
  const out: { v: number; flags?: KnownFlags; agents?: number } = { v: VERSION };
  if (r.flags) {
    // Keys in one order, so the same switches always serialize the same.
    const flags: KnownFlags = {};
    for (const key of NAV_FLAG_KEYS) if (r.flags[key] !== undefined) flags[key] = r.flags[key];
    out.flags = flags;
  }
  if (r.agents !== undefined) out.agents = r.agents;
  return JSON.stringify(out);
}

/** localStorage, or null where it cannot be reached (node, a locked-down browser). */
export function browserStorage(): NavStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readRemembered(customerId: string | null | undefined, storage: NavStorage | null = browserStorage()): RememberedNav | undefined {
  if (!customerId || !storage) return undefined;
  try {
    return parseRemembered(storage.getItem(navFlagsStorageKey(customerId)));
  } catch {
    return undefined;
  }
}

/** Change what this workspace left behind; writes only when it differs. */
function remember(customerId: string, change: (before: RememberedNav) => RememberedNav, storage: NavStorage | null): void {
  if (!customerId || !storage) return;
  try {
    const key = navFlagsStorageKey(customerId);
    const before = storage.getItem(key);
    const next = serialize(change(parseRemembered(before) ?? {}));
    if (next !== before) storage.setItem(key, next);
  } catch {
    /* storage full, blocked or gone: nothing remembered, nothing broken */
  }
}

/** Keep a settings answer's switches (they replace the remembered ones;
 *  a remembered `machines_enabled` / `connections_enabled` stays unless the answer carries one). */
export function rememberNavFlags(customerId: string, live: KnownFlags, storage: NavStorage | null = browserStorage()): void {
  remember(customerId, (before) => ({ ...before, flags: chooseNavFlags(live, before.flags) }), storage);
}

/** Keep the machines switch's answer beside the other switches. */
export function rememberMachinesFlag(customerId: string, enabled: boolean, storage: NavStorage | null = browserStorage()): void {
  remember(customerId, (before) => ({ ...before, flags: { ...before.flags, machines_enabled: enabled } }), storage);
}

/** Keep the connections switch's answer (the list's 409 or 200) beside the other switches. */
export function rememberConnectionsFlag(customerId: string, enabled: boolean, storage: NavStorage | null = browserStorage()): void {
  remember(customerId, (before) => ({ ...before, flags: { ...before.flags, connections_enabled: enabled } }), storage);
}

/** Keep how many agents the workspace line counts. */
export function rememberAgentCount(customerId: string, agents: number, storage: NavStorage | null = browserStorage()): void {
  if (!Number.isInteger(agents) || agents < 0) return;
  remember(customerId, (before) => ({ ...before, agents }), storage);
}
